import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, interval, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { L10nService } from '../l10n.service';

export interface VersionInfo {
    version: string;
    buildTime?: string;
}

@Injectable({
    providedIn: 'root'
})
export class VersionService {
    private readonly CHECK_INTERVAL_MS = 60 * 60 * 1000;
    private readonly RESHOW_AFTER_DISMISS_MS = 30 * 60 * 1000;
    private readonly VERSION_URL = '/assets/version.json';
    
    private currentVersion: string;
    private updateAvailable$ = new BehaviorSubject<boolean>(false);
    private dismissedAt: number | null = null;

    constructor(private http: HttpClient, private l10nService: L10nService) {
        this.currentVersion = environment.version;
    }

    public startVersionChecking(): void {
        this.checkVersion();

        interval(this.CHECK_INTERVAL_MS).subscribe(() => {
            this.checkVersion();
        });
    }

    public get onUpdateAvailable(): Observable<boolean> {
        return this.updateAvailable$.asObservable();
    }

    private checkVersion(): void {
        const url = window.location.href.includes('localhost') 
            ? `${this.VERSION_URL}?t=${new Date().getTime()}`
            : `/spa/${this.l10nService.getCurrentLang()}${this.VERSION_URL}?t=${new Date().getTime()}`;
        
        this.http.get<VersionInfo>(url, {
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            }
        }).pipe(
            map(versionInfo => {
                if (this.currentVersion !== versionInfo.version) {
                    console.log('Newer version detected - Current:', this.currentVersion, 'Available:', versionInfo.version);
                    const shouldShow = !this.dismissedAt || 
                                     (Date.now() - this.dismissedAt) > this.RESHOW_AFTER_DISMISS_MS;
                    
                    if (shouldShow) {
                        this.updateAvailable$.next(true);
                    }
                    return true;
                }
                return false;
            }),
            catchError(error => {
                console.error('Error checking version:', error);
                return [];
            })
        ).subscribe();
    }

    public getCurrentVersion(): string {
        return this.currentVersion;
    }

    public dismissBanner(): void {
        this.dismissedAt = Date.now();
        this.updateAvailable$.next(false);
    }
}
